import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Zap, RefreshCw, Wind, TreePine, Brain, ArrowRight, Trophy, Flame, Sparkles, Lock } from 'lucide-react';

const GAMES = [
  { key: 'thought_defense', path: '/games/thought-defense', icon: Zap,    title: 'Thought Defense', tagline: '60-second mind reflex', body: 'Swipe negative thoughts away. Protect the positive ones. Trains real-time thought awareness.', color: '#D4A437' },
  { key: 'reframe_battle',  path: '/games/reframe-battle',  icon: RefreshCw, title: 'Reframe Battle',  tagline: 'CBT in disguise',     body: 'Real scenarios. Pick the strongest reframe. Mick reacts to every choice.', color: '#8A4FFF' },
  { key: 'breath_rhythm',   path: '/games/breath-rhythm',   icon: Wind,    title: 'Breath Rhythm',   tagline: '4-7-8 breathing',     body: 'Pulsing orb. Soft tones. Reset your nervous system in 1, 3, or 5 minutes.', color: '#3A6FB0' },
  { key: 'streak_tree',     path: '/games/streak-tree',     icon: TreePine, title: 'Mindset Tree',    tagline: 'Watch yourself grow',  body: 'Every check-in drops a gold leaf on your tree. Miss days, leaves fall. Shareable.', color: '#2F8F4F' },
  { key: 'inner_battle',    path: '/games/inner-battle',    icon: Brain,   title: 'Inner Battle',    tagline: 'Your brain, visualized', body: 'A living brain that transforms based on your daily work. Cracked to clean. Foggy to gold.', color: '#B42318' },
];

export default function GamesHub() {
  const { user } = useAuth();
  const [splash, setSplash] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/splash').then(r => setSplash(r.data));
    if (user) api.get('/games/state').then(r => setStats(r.data)).catch(() => {});
  }, [user]);

  const enabled = splash?.games_enabled || {};
  const list = GAMES.filter(g => enabled[g.key] !== false);

  return (
    <div className="bg-white">
      <section className="relative bg-ink text-white py-14 md:py-20 overflow-hidden" data-testid="games-hub">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50"/>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 400px at 75% 50%, rgba(212,164,55,0.18), transparent 60%)' }}/>
        <div className="relative kc-container grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Sparkles size={12}/> The Mind Maze</div>
            <h1 className="mt-4 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }}>TRAIN YOUR<br/><span className="text-[color:var(--kc-gold)]">MIND.</span></h1>
            <p className="mt-4 text-lg text-white/80 max-w-xl leading-relaxed">Small daily games that teach awareness, reframing, breath, and discipline — disguised as fun. Pick one. Play 60 seconds. Watch your mind get sharper.</p>
          </div>
          {user && stats && (
            <div className="lg:col-span-5">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[var(--kc-radius-md)] p-5 grid grid-cols-3 gap-2">
                <Tile label="XP" value={stats.xp || 0} icon={Trophy}/>
                <Tile label="Level" value={stats.level || 1} icon={Sparkles}/>
                <Tile label="Streak" value={`${stats.streak || 0}d`} icon={Flame}/>
              </div>
              <Link to="/games/leaderboard" className="mt-3 flex items-center justify-between gap-3 px-4 h-12 rounded-[var(--kc-radius-md)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="gh-leaderboard-cta">
                <span className="inline-flex items-center gap-2"><Trophy size={16}/> See the Leaderboard</span>
                <ArrowRight size={16}/>
              </Link>
            </div>
          )}
          {!user && (
            <div className="lg:col-span-5">
              <Link to="/games/leaderboard" className="flex items-center justify-between gap-3 px-4 h-12 rounded-[var(--kc-radius-md)] border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors" data-testid="gh-leaderboard-cta-public">
                <span className="inline-flex items-center gap-2"><Trophy size={16} className="text-[color:var(--kc-gold)]"/> See the Leaderboard</span>
                <ArrowRight size={16}/>
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="kc-container">
          {list.length === 0 ? (
            <div className="text-center py-12 bg-surface-2 rounded-[var(--kc-radius-md)] border border-kc" data-testid="games-empty">
              <Lock className="mx-auto text-muted-kc mb-3" size={36}/>
              <h3 className="font-display uppercase text-2xl">No games active right now.</h3>
              <p className="mt-2 text-muted-kc">Mickey can turn games on or off in the admin panel.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((g) => { const Icon = g.icon; return (
                <Link key={g.key} to={g.path} className="group relative bg-white border border-kc-strong hover:border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] p-6 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(212,164,55,0.4)] overflow-hidden" data-testid={`game-card-${g.key}`}>
                  <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 blur-2xl" style={{ background: g.color }}/>
                  <div className="relative">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: `${g.color}20`, color: g.color }}><Icon size={26}/></div>
                    <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">{g.tagline}</div>
                    <h3 className="mt-1 font-display uppercase text-2xl leading-tight">{g.title}</h3>
                    <p className="mt-2 text-sm text-ink-2 leading-relaxed">{g.body}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider group-hover:gap-2 transition-all" style={{ color: g.color }}>Play <ArrowRight size={14}/></div>
                  </div>
                </Link>
              ); })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const Tile = ({ label, value, icon: Icon }) => (
  <div className="text-center py-2"><Icon className="mx-auto text-[color:var(--kc-gold)] mb-1" size={16}/><div className="font-display text-2xl text-[color:var(--kc-gold)] tabular-nums">{value}</div><div className="text-[10px] tracking-[0.18em] uppercase text-white/60">{label}</div></div>
);
